import { Flag, Target } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDateValue, formatNumber } from '@/lib/utils';

import type { ActiveGoal, GoalsDashboard } from '../dashboard.api';

interface ActiveGoalsPanelProps {
  data: GoalsDashboard | undefined;
  loading: boolean;
}

const COLORES: Record<string, string> = {
  completed: 'bg-emerald-500',
  on_track: 'bg-brand-500',
  warning: 'bg-amber-500',
  behind: 'bg-rose-500',
  expired: 'bg-ink-400',
};

const ETIQUETAS: Record<string, string> = {
  completed: 'Cumplida',
  on_track: 'En camino',
  warning: 'Atención',
  behind: 'Atrasada',
  expired: 'Vencida',
};

/** Cuántas metas se muestran directamente en la tarjeta. */
const PREVIEW_LIMIT = 4;

/** Fila de una meta: nombre, combustible, vencimiento y barra de avance. */
function GoalRow({ meta }: { meta: ActiveGoal }) {
  const avance = Math.min(Math.max(Number(meta.progress_percentage || 0), 0), 100);
  const color = COLORES[meta.performance_status] ?? 'bg-brand-500';

  return (
    <li>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink-800">{meta.goal_name}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-ink-400">
            <span className="inline-flex items-center gap-1">
              <Flag className="size-3" aria-hidden="true" />
              {meta.fuel_grade_name ?? 'Todos los productos'}
            </span>
            <span>Vence {formatDateValue(meta.end_date)}</span>
            <span>
              {Number(meta.days_remaining ?? 0) > 0
                ? `Quedan ${formatNumber(meta.days_remaining)} días`
                : 'Plazo cumplido'}
            </span>
          </p>
        </div>

        <Badge
          variant={
            meta.performance_status === 'completed'
              ? 'success'
              : meta.performance_status === 'on_track'
                ? 'brand'
                : meta.performance_status === 'warning'
                  ? 'warning'
                  : meta.performance_status === 'behind'
                    ? 'danger'
                    : 'neutral'
          }
        >
          {ETIQUETAS[meta.performance_status] ?? meta.performance_status}
        </Badge>
      </div>

      <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-ink-100">
        <div
          role="progressbar"
          aria-valuenow={Math.round(avance)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Avance de ${meta.goal_name}`}
          style={{ width: `${avance}%` }}
          className={cn('h-full rounded-full transition-all duration-500', color)}
        />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-500">
        <span className="font-mono tabular-nums">
          {formatNumber(meta.current_value)} / {formatNumber(meta.target_value)}{' '}
          {meta.unit_measurement ?? ''}
        </span>
        <span className="font-semibold text-ink-700">{avance.toFixed(1)}%</span>
      </div>
    </li>
  );
}

/** Metas activas con su avance actual. */
export function ActiveGoalsPanel({ data, loading }: ActiveGoalsPanelProps) {
  const [abierto, setAbierto] = useState(false);
  const metas = useMemo<ActiveGoal[]>(() => data?.active_goals ?? [], [data]);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton key={index} className="h-20 rounded-control" />
        ))}
      </div>
    );
  }

  if (metas.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-ink-200 px-4 py-8 text-center">
        <Target className="mx-auto size-5 text-ink-300" aria-hidden="true" />
        <p className="mt-2 text-sm font-medium text-ink-700">No hay metas activas</p>
        <p className="mt-0.5 text-xs text-ink-500">
          Las metas se administran en el módulo de metas del panel.
        </p>
      </div>
    );
  }

  const vistaPrevia = metas.slice(0, PREVIEW_LIMIT);

  return (
    <div className="flex h-full flex-col gap-4">
      <ul className="space-y-4">
        {vistaPrevia.map((meta) => (
          <GoalRow key={meta.id} meta={meta} />
        ))}
      </ul>

      {metas.length > PREVIEW_LIMIT ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAbierto(true)}
          className="self-start"
        >
          Ver todos ({metas.length})
        </Button>
      ) : null}

      {data?.stats ? (
        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-ink-100 pt-3">
          <Badge variant="brand">{formatNumber(data.stats.total_active ?? 0)} activas</Badge>
          <Badge variant="success">{formatNumber(data.stats.total_completed ?? 0)} cumplidas</Badge>
          {Number(data.stats.at_risk ?? 0) > 0 ? (
            <Badge variant="warning">{formatNumber(data.stats.at_risk)} en riesgo</Badge>
          ) : null}

          {Number(data.stats.avg_completion ?? 0) > 0 ? (
            <span className="ml-auto text-xs text-ink-500">
              Avance medio{' '}
              <strong className="font-semibold text-ink-700">
                {Number(data.stats.avg_completion).toFixed(1)}%
              </strong>
            </span>
          ) : null}
        </div>
      ) : null}

      <Dialog
        open={abierto}
        onClose={() => setAbierto(false)}
        title="Metas activas"
        description={`${metas.length} metas con avance actual`}
        size="lg"
      >
        <ul className="space-y-4">
          {metas.map((meta) => (
            <GoalRow key={meta.id} meta={meta} />
          ))}
        </ul>
      </Dialog>
    </div>
  );
}
